import React, { useRef, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  FormGroup,
  Label,
  Row,
} from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import {
  addMember,
  getNextOfKinByIdApi,
  recordMemberDeclaration,
  uploadDepeMedia,
  uploadMemberAttachment,
} from 'api/member';
import { getDependentsByIdApi } from 'api/claim';
import SignaturePad from './SignaturePad';
import { getDeclarationText } from './ConsentGate';

const CompleteRegister = ({
  member,
  consentDocument,
  memberData,
  pendingNok,
  pendingDependants,
  onMemberCreated,
  onMemberId,
}) => {
  const navigate = useNavigate();
  const signatureRef = useRef(null);
  // Survives a failed retry so a second click never creates a second member.
  const createdIdRef = useRef(member?.memberId || null);
  const [accepted, setAccepted] = useState(true);
  const [signatureCaptured, setSignatureCaptured] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const uploadStagedFiles = async (memberId) => {
    if (pendingNok?.file) {
      try {
        const kins = await getNextOfKinByIdApi({ memberId, status: 'Active' });
        const kinId = (kins || []).find((k) => k.status === 'Active')?.nextOfKinId || kins?.[0]?.nextOfKinId;
        if (kinId) await uploadDepeMedia(kinId, pendingNok.file);
      } catch (e) { /* non-fatal: NOK document can be uploaded later */ }
    }
    const withFiles = (pendingDependants || []).filter((d) => d.file);
    if (withFiles.length) {
      try {
        const saved = (await getDependentsByIdApi({ memberId })) || [];
        for (const d of withFiles) {
          const match = saved.find(
            (x) => x.firstName === d.payload.firstName && x.lastName === d.payload.lastName && x.dob === d.payload.dob,
          );
          if (match?.dependentId) await uploadDepeMedia(match.dependentId, d.file);
        }
      } catch (e) { /* non-fatal */ }
    }
  };

  const submitDeclaration = async () => {
    if (!accepted || !signatureCaptured || submitting) return;
    if (!member?.memberId && !memberData) return;
    setSubmitting(true);
    setError(null);
    try {
      let memberId = member?.memberId || createdIdRef.current;
      if (!memberId) {
        const res = await addMember({
          ...memberData,
          status: 'Draft',
          nextOfKin: pendingNok?.payload || null,
          dependents: (pendingDependants || []).map((d) => d.payload),
        });
        memberId = res?.data;
        if (!memberId) throw new Error(res?.message || 'The member could not be created.');
        createdIdRef.current = memberId;
        if (typeof onMemberId === 'function') onMemberId(memberId);
        if (typeof onMemberCreated === 'function') await onMemberCreated(memberId);
        await uploadStagedFiles(memberId);
      }
      const signatureFile = await signatureRef.current.toFile();
      const uploadResponse = await uploadMemberAttachment(memberId, signatureFile, 'Declaration');
      const signatureUrl =
        uploadResponse?.url ||
        uploadResponse?.data?.url ||
        uploadResponse?.data?.[0]?.url ||
        uploadResponse?.[0]?.url;
      if (!signatureUrl) throw new Error('The signature was uploaded but no file URL was returned.');
      await recordMemberDeclaration({
        memberId,
        facilitatorId: Number(localStorage.getItem('userId')) || 0,
        consentDocumentId:
          consentDocument?.declarationDocumentId || consentDocument?.consentDocumentId,
        channel: 'FACILITATOR',
        declarationAccepted: true,
        signatureUrl,
        signatureCaptured: true,
        deviceId: window.navigator?.userAgent || '',
        remarks: '',
      });
      navigate('/members');
    } catch (err) {
      setError(err?.message || 'The declaration could not be submitted. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const declarationText = getDeclarationText(consentDocument?.document);

  return (
    <div className="row justify-content-center">
      <Col lg="8">
        <h4 className="mb-3">Declaration and confirmation</h4>
        <p className="text-muted">Review the declaration with the member before submitting the registration.</p>
        <Alert color="light" className="border lh-lg">
          {declarationText || 'The active consent document does not contain declaration text.'}
        </Alert>
        {error ? <Alert color="danger">{error}</Alert> : null}
        <FormGroup className="form-check mb-4">
          <label className="form-check-label d-flex align-items-start gap-2" htmlFor="memberDeclaration">
            <input
              id="memberDeclaration"
              className="form-check-input flex-shrink-0 mt-1"
              type="checkbox"
              checked={accepted}
              onChange={(event) => setAccepted(event.currentTarget.checked)}
            />
            The member confirms this declaration and accepts the SUN Welfare policies, terms, and conditions.
          </label>
        </FormGroup>
        <Label className="fw-semibold">Member signature</Label>
        <SignaturePad ref={signatureRef} disabled={submitting} onChange={setSignatureCaptured} />
        <Button className="mt-4" color="primary" disabled={!accepted || !signatureCaptured || !declarationText || submitting} onClick={submitDeclaration}>
          {submitting ? 'Submitting...' : 'Confirm and finish'}
        </Button>
      </Col>
    </div>
  );
};

export default CompleteRegister;
